/* eslint-disable react/jsx-one-expression-per-line */
import React, { useContext } from "react";
import _ from "lodash";
import { currentLanguage, getTranslateByScope } from "../../../i18n";
import AppContext from "../../../AppContext";
import { Bold } from "../../../Components";
import "./QuantitySummary.scss";
import {
  getProductsByNumber,
  getCurrentOrderCustomerPriceGroup,
  getPriceFromCache,
  getEanForColorAndSize,
} from "../../../selectors";

const translate = getTranslateByScope("app.closeOrder");

const valForOrderLineItem = (context, v) => {
  let sum = 0;
  const d = v.data;
  const { number } = v;
  const { season } = v;
  const priceGroup = getCurrentOrderCustomerPriceGroup(context);

  if (d == null) {
    return 0;
  }

  let currency = "";
  const lengths = Object.keys(d);
  lengths.forEach((l) => {
    const colors = Object.keys(d[l]);
    colors.forEach((c) => {
      const sizes = Object.keys(d[l][c]);
      sizes.forEach((s) => {
        const count = parseInt(d[l][c][s]);
        const ean = getEanForColorAndSize({
          number,
          context,
          color: c,
          size: s,
        });
        const price = getPriceFromCache({
          number,
          context,
          priceGroup,
          season,
          ean,
        });

        if (count > 0 && price && price.value > 0) {
          sum += count * price.value;
          currency = price.currency;
        }
      });
    });
  });

  return { sum, currency };
};
const sumForOrderLineItem = (v) => {
  let sum = 0;
  const d = v.data;

  if (d == null) {
    return 0;
  }

  const lengths = Object.keys(d);
  lengths.forEach((l) => {
    const colors = Object.keys(d[l]);
    colors.forEach((c) => {
      const sizes = Object.keys(d[l][c]);
      sizes.forEach((s) => {
        sum += parseInt(d[l][c][s]);
      });
    });
  });

  return sum;
};

const getSelectedProductData = (products, productKeys) => {
  let selectedProductsObject = {};
  productKeys.forEach((key) => {
    selectedProductsObject[products[key].number] = products[key].data;
  });
  return selectedProductsObject;
};

const getUniqueProductSumForItemNumber = (selectedProductData) => {
  let uniqueProductsAmount = {};
  for (let productNumber in selectedProductData) {
    for (let length in selectedProductData[productNumber]) {
      for (let color in selectedProductData[productNumber][length])
        for (let size in selectedProductData[productNumber][length][color]) {
          if (!uniqueProductsAmount[productNumber]) {
            uniqueProductsAmount[productNumber] = 0;
          }
          if (selectedProductData[productNumber][length][color][size] > 0) {
            uniqueProductsAmount[productNumber] += 1;
            break;
          }
        }
    }
  }

  return uniqueProductsAmount;
};

const getTotalUniqueProductsSum = (cartDataMinimized) => {
  let totalUniqueProductsSum = 0;
  for (let topLevelCategory in cartDataMinimized) {
    totalUniqueProductsSum += cartDataMinimized[topLevelCategory].uniqueItems;
  }

  return totalUniqueProductsSum;
};

export const QuantitySummary = ({ orderData }) => {
  const context = useContext(AppContext);

  if (_.isNil(orderData) || _.isNil(orderData.products)) {
    return null;
  }

  const { id } = orderData;
  const { products } = orderData;
  const productKeys = Object.keys(products);

  const lang = currentLanguage();

  const data = {};
  const colsSeen = {};
  const summary = {};

  _.compact(productKeys).forEach((k) => {
    const v = products[k];
    const item_no = v.number;
    const p = getProductsByNumber(context)[item_no];

    if (!_.isNil(p)) {
      let { col, hwg: hwgObj } = p;
      if (!col) {
        col = "??";
      }

      const hwg = hwgObj ? hwgObj[lang] : "Unknown";

      const selectedProductData = getSelectedProductData(products, productKeys);
      const uniqueProductSumForItemNumber =
        getUniqueProductSumForItemNumber(selectedProductData);

      // it's a set ;)
      colsSeen[col] = 1;

      if (!data[hwg]) {
        data[hwg] = {};
      }

      if (!data[hwg][col]) {
        data[hwg][col] = { sum: 0, quantity: 0 };
      }

      if (!summary[col]) {
        summary[col] = { quantity: 0, sum: 0 };
      }

      const s = sumForOrderLineItem(v);
      data[hwg][col].quantity += s;
      summary[col].quantity += s;

      if (!data[hwg].uniqueItems) {
        data[hwg].uniqueItems = 0;
      }

      if (uniqueProductSumForItemNumber[item_no]) {
        data[hwg].uniqueItems += uniqueProductSumForItemNumber[item_no];
      }
      const x = valForOrderLineItem(context, v);

      if (x.sum > 0) {
        data[hwg][col].sum += x.sum;
        summary[col].sum += x.sum;
      }
    }
  });

  if (Object.keys(data).length === 0) {
    return null;
  }

  const cols = Object.keys(colsSeen);
  const hwgs = Object.keys(data);
  const colCount = cols.length;
  const caps = (x) => x.charAt(0).toUpperCase() + x.substring(1);

  return (
    <div key={`ordersum${id}`}>
      <Bold>{translate("collections")}</Bold>
      <table className="qsTable">
        <thead>
          <tr>
            <th />
            <th className="qsHead" colSpan={colCount}>
              {translate("quantity")}
            </th>
            <th className="qsHead" colSpan={colCount}>
              {translate("pieces")}
            </th>
            <th className="qsHead" colSpan={colCount}>
              {translate("amount")}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td />
            <td className="qsCol" colSpan={colCount}></td>
            {cols.map((s) => (
              <td className="qsCol" key={`col-q-${s}`}>
                {s}
              </td>
            ))}
            {cols.map((s) => (
              <td className="qsCol" key={`col-s-${s}`}>
                {s}
              </td>
            ))}
          </tr>
          {hwgs.map((hwg, i) => (
            <tr className="qsRow" key={`row-${hwg}`}>
              <td className="qsHwg">{caps(hwg)}</td>
              <td colSpan={colCount}>{data[hwg].uniqueItems}</td>
              {cols.map((col) => {
                let quantity = 0;
                if (data[hwg] && data[hwg][col]) {
                  quantity = data[hwg][col].quantity;
                }
                return <td key={`${hwg}-${col}-q`}>{Math.round(quantity)}</td>;
              })}

              {cols.map((col) => {
                let sum = 0;
                if (data[hwg] && data[hwg][col]) {
                  sum = data[hwg][col].sum;
                }
                return <td key={`${hwg}-${col}-s`}>{sum.toFixed(2)}</td>;
              })}
            </tr>
          ))}
          <tr className="qsFooter">
            <td>{translate("total")}</td>
            <td className="qsCol" colSpan={colCount}>
              {getTotalUniqueProductsSum(data)}
            </td>
            {cols.map((col) => (
              <td className="qsCol" key={`col-sum-${col}-quantity`}>
                {Math.round(summary[col].quantity)}
              </td>
            ))}
            {cols.map((col) => (
              <td className="qsCol" key={`col-sum-${col}-sum`}>
                {summary[col].sum.toFixed(2)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
